import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Link } from "components/ui/Link";
import { Loading } from "components/ui/Loading/Loading";
import { type BadgeTone, StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import {
  type DeliveryItem,
  useCancelDeliveryApi,
  useDeleteDeliveryApi,
  useDeliveriesApi,
} from "hooks/use-delivery-api";
import { Eye, RefreshCw } from "lucide-react";
import { type FC, useState } from "react";
import { useNavigate } from "react-router-dom";

type TabStatus = "draft" | "scheduled" | "history";

const STATUS_BADGE: Record<string, { label: string; tone: BadgeTone }> = {
  sent: { label: "送信完了", tone: "soft-success" },
  // biome-ignore lint/style/useNamingConvention: matches backend DeliveryStatus enum value
  partially_sent: { label: "一部失敗", tone: "soft-warning" },
  failed: { label: "失敗", tone: "critical-error" },
  sending: { label: "送信中", tone: "soft-info" },
  scheduled: { label: "予約中", tone: "soft-info" },
  draft: { label: "下書き", tone: "soft-neutral" },
  cancelled: { label: "キャンセル", tone: "soft-neutral" },
};

export const DeliveriesPage: FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabStatus>("draft");
  const [page, setPage] = useState(1);

  const { data, loading, error, refetch } = useDeliveriesApi({
    status: tab,
    page,
    limit: 20,
  });
  const { deleteDelivery } = useDeleteDeliveryApi();
  const { cancelDelivery } = useCancelDeliveryApi();

  const [confirmAction, setConfirmAction] = useState<{
    type: "delete" | "cancel";
    id: string;
  } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const handleTabChange = (newTab: TabStatus) => {
    setTab(newTab);
    setPage(1);
  };

  const handleAction = async () => {
    if (!confirmAction) return;
    setActionLoading(true);
    try {
      if (confirmAction.type === "delete") {
        await deleteDelivery(confirmAction.id);
      } else {
        await cancelDelivery(confirmAction.id);
      }
      setConfirmAction(null);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  const totalPages = data ? Math.ceil(data.total / 20) : 1;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>一斉配信</PageTitle>
        <div className="alert alert-error">
          <span>エラーが発生しました: {error.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <PageTitle>一斉配信</PageTitle>
        <Button
          type="button"
          size="sm"
          onClick={() => navigate("/deliveries/new")}
        >
          + 新規配信
        </Button>
      </div>

      {/* タブ */}
      <div className="flex gap-6 border-b border-slate-200 mb-6">
        {(
          [
            { key: "draft", label: "下書き" },
            { key: "scheduled", label: "予約中" },
            { key: "history", label: "配信履歴" },
          ] as const
        ).map(t => {
          const isActive = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => handleTabChange(t.key)}
              className={`relative px-2 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "text-primary"
                  : "text-base-content/60 hover:text-base-content"
              }`}
            >
              {t.label}
              {isActive && (
                <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-primary" />
              )}
            </button>
          );
        })}
      </div>

      {/* テーブル */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <Table.Root density="compact" zebra stickyHeader>
          <Table.Head sticky>
            <tr>
              <Table.HeaderCell>件名</Table.HeaderCell>
              <Table.HeaderCell>配信先グループ</Table.HeaderCell>
              <Table.HeaderCell>送信者</Table.HeaderCell>
              {tab === "history" && <Table.HeaderCell>宛先数</Table.HeaderCell>}
              {tab === "scheduled" && (
                <Table.HeaderCell>予約日時</Table.HeaderCell>
              )}
              {tab === "history" && (
                <Table.HeaderCell>配信日時</Table.HeaderCell>
              )}
              {tab === "history" && (
                <Table.HeaderCell>ステータス</Table.HeaderCell>
              )}
              {tab === "draft" && <Table.HeaderCell>作成日時</Table.HeaderCell>}
              {tab === "scheduled" && (
                <Table.HeaderCell>作成日時</Table.HeaderCell>
              )}
              <Table.HeaderCell>操作</Table.HeaderCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {loading ? (
              <tr>
                <td
                  colSpan={tab === "history" ? 7 : 5}
                  className="text-center py-8"
                >
                  <Loading size="md" />
                </td>
              </tr>
            ) : data?.items.length === 0 ? (
              <tr>
                <td
                  colSpan={tab === "history" ? 7 : 5}
                  className="text-center py-8 text-base-content/60"
                >
                  {tab === "draft" && "下書きがありません"}
                  {tab === "scheduled" && "予約中の配信がありません"}
                  {tab === "history" && "配信履歴がありません"}
                </td>
              </tr>
            ) : (
              data?.items.map((item: DeliveryItem) => (
                <Table.Row key={item.id}>
                  <Table.Cell>
                    <Link
                      to={
                        tab === "draft"
                          ? `/deliveries/${item.id}/edit`
                          : `/deliveries/${item.id}`
                      }
                    >
                      {item.subject}
                    </Link>
                  </Table.Cell>
                  <Table.Cell>{item.groupName || "-"}</Table.Cell>
                  <Table.Cell>{item.fromName || item.fromEmail}</Table.Cell>
                  {tab === "history" && (
                    <Table.Cell>
                      <span className="text-success">{item.successCount}</span>/
                      <span className="text-error">{item.failureCount}</span>
                      <span className="text-base-content/60">
                        {" "}
                        ({item.totalRecipients})
                      </span>
                    </Table.Cell>
                  )}
                  {tab === "scheduled" && (
                    <Table.Cell>{formatDate(item.scheduledAt)}</Table.Cell>
                  )}
                  {tab === "history" && (
                    <Table.Cell>{formatDate(item.sentAt)}</Table.Cell>
                  )}
                  {tab === "history" && (
                    <Table.Cell>
                      <StatusBadge
                        tone={STATUS_BADGE[item.status]?.tone ?? "soft-neutral"}
                      >
                        {STATUS_BADGE[item.status]?.label || item.status}
                      </StatusBadge>
                    </Table.Cell>
                  )}
                  {(tab === "draft" || tab === "scheduled") && (
                    <Table.Cell>{formatDate(item.createdAt)}</Table.Cell>
                  )}
                  <Table.Cell>
                    <div className="flex gap-2">
                      {tab === "draft" && (
                        <>
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            onClick={() =>
                              navigate(`/deliveries/${item.id}/edit`)
                            }
                          >
                            編集
                          </Button>
                          <Button
                            type="button"
                            onClick={() =>
                              setConfirmAction({
                                type: "delete",
                                id: item.id,
                              })
                            }
                            variant="destructive"
                            size="xs"
                          >
                            削除
                          </Button>
                        </>
                      )}
                      {tab === "scheduled" && (
                        <Button
                          type="button"
                          onClick={() =>
                            setConfirmAction({
                              type: "cancel",
                              id: item.id,
                            })
                          }
                          variant="warning"
                          size="xs"
                        >
                          キャンセル
                        </Button>
                      )}
                      {tab === "history" && (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            onClick={() => navigate(`/deliveries/${item.id}`)}
                          >
                            <Eye size={14} />
                            詳細
                          </Button>
                          <Button
                            type="button"
                            size="xs"
                            onClick={() =>
                              navigate(`/deliveries/new?copy-from=${item.id}`)
                            }
                          >
                            <RefreshCw size={14} />
                            再配信
                          </Button>
                        </>
                      )}
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </div>

      {/* ページネーション */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <div className="join">
            <Button
              type="button"
              size="sm"
              isJoinItem
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              &laquo;
            </Button>
            <Button type="button" size="sm" isJoinItem>
              {page} / {totalPages}
            </Button>
            <Button
              type="button"
              size="sm"
              isJoinItem
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              &raquo;
            </Button>
          </div>
        </div>
      )}

      {/* 確認モーダル */}
      {confirmAction && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">
              {confirmAction.type === "delete"
                ? "配信を削除しますか？"
                : "予約をキャンセルしますか？"}
            </h3>
            <p className="py-4">この操作は取り消せません。</p>
            <div className="modal-action">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setConfirmAction(null)}
                disabled={actionLoading}
              >
                戻る
              </Button>
              <Button
                type="button"
                variant={
                  confirmAction.type === "delete" ? "destructive" : "warning"
                }
                onClick={handleAction}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <Loading size="sm" />
                ) : confirmAction.type === "delete" ? (
                  "削除"
                ) : (
                  "キャンセル"
                )}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !actionLoading && setConfirmAction(null)}
          />
        </div>
      )}
    </div>
  );
};
